<template>
    <DsMultiSelect
        v-model="selectedClients"
        v-bind="$attrs"
        :id
        ref="clientMultiSelectRef"
        :disableSelectAll
        :label
        :options="clientOptions"
        :placeholder
        :required
    >
        <template #optionLabel="{ option }">
            <ClientMultiSelectOption
                :disabled="option.disabled"
                :email="option.email"
                :first="option.first"
                :helperText="option.helperText"
                :last="option.last"
                :role="option.role"
            >
                <template #subtitle>
                    {{ option.phone ? `${option.email}, ${formatE164ToNational(option.phone)}` : option.email }}
                </template>
                <template #trailing>
                    <EditClientButton :clientId="option.value" />
                </template>
            </ClientMultiSelectOption>
        </template>

        <template #chipContent="{ option, truncate }">
            <span>
                {{ truncate(option.first && option.last ? joinFirstLast(option.first, option.last) : option.email) }}
            </span>
        </template>

        <template #emptyState>
            <NoClientsAdded
                v-if="!clientOptions.length"
                @addClients="scrollToClientsSection"
            />
        </template>
    </DsMultiSelect>
    <!-- @todo [CLENG-1942] remove hidden input needed for form submission -->
    <input
        v-for="client in selectedClients"
        :key="`${name}-${client.id}`"
        :name
        type="hidden"
        :value="client.value"
    />
</template>

<script setup lang="ts">
import { registerAddFileSubmittedAction } from '@/company-portal/add-file/add-file-reset';
import { resetAvailableClients } from '@/company-portal/add-file/available-clients';
import type { ClientMultiSelectName } from '@/company-portal/add-file/selectors';
import { useAddFileFormScroller } from '@/company-portal/add-file/use-add-file-form-scroller';
import NoClientsAdded from '@/components/Views/Company/shared/NoClientsAdded.vue';
import DsMultiSelect from '@/components/design-system/multiselect/DsMultiSelect.vue';
import EditClientButton from '@/components/Views/Company/AddFile/EditClient/EditClientButton.vue';
import ClientMultiSelectOption from '@/components/Views/Company/File/ClientMultiSelectOption.vue';
import type { ClientOption } from '@/components/Views/Company/File/file.types';
import { formatE164ToNational } from '@/shared/utils/phone-number';
import { joinFirstLast } from '@/shared/utils/strings';
import { computed, onUnmounted, useId, useTemplateRef, watch } from 'vue';

export interface ClientMultiSelectProps {
    clientOptions: ClientOption[];
    label?: string;
    name?: ClientMultiSelectName;
    placeholder?: string;
    required?: boolean;
}

const props = withDefaults(defineProps<ClientMultiSelectProps>(), {
    label: 'Client(s)',
    placeholder: 'Select client(s)',
    required: true,
});

const id = useId();

const selectedClients = defineModel<ClientOption[]>({ default: [] });

const clientOptionsMap = computed(() => new Map(props.clientOptions.map(client => [client.id, client])));

const disableSelectAll = computed(() => props.clientOptions.some(option => option.disabled));

// Remove any selected clients that are no longer present in the available options
// (e.g. a client's email was cleared, dropping them from the list)
watch(clientOptionsMap, options => {
    const filtered = selectedClients.value.filter(client => options.has(client.id));
    if (filtered.length !== selectedClients.value.length) {
        selectedClients.value = filtered;
    }
});

// Empty state
const fileFormScroller = useAddFileFormScroller();

const clientMultiSelectRef = useTemplateRef('clientMultiSelectRef');

const scrollToClientsSection = () => {
    clientMultiSelectRef.value?.handleOpen(false);
    fileFormScroller.scrollToClientSection();
};

// Form reset
const unregisterAddFileSubmittedAction = registerAddFileSubmittedAction(() => {
    selectedClients.value = [];
    resetAvailableClients(); // Needed to clear the legacy Documents > Recipients dropdown. Remove with CLENG-2932.
});

onUnmounted(unregisterAddFileSubmittedAction);
</script>
